import React, { useState, useEffect } from 'react';
import { X, ArrowRightLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export default function TransferenciaModal({ onClose, onSaved, carteiras: carteirasProps }) {
  const [carteiras, setCarteiras] = useState(carteirasProps || []);
  const [form, setForm] = useState({
    carteiraOrigemId: '',
    carteiraDestinoId: '',
    valor: '',
    descricao: '',
    dataCompetencia: new Date().toISOString().slice(0, 10),
    observacoes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!carteirasProps || carteirasProps.length === 0) {
      api.get('/carteiras').then(r => setCarteiras(r.data)).catch(() => {});
    }
  }, [carteirasProps]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const origemNome = carteiras.find(c => c.id === form.carteiraOrigemId)?.nome || '';
  const destinoNome = carteiras.find(c => c.id === form.carteiraDestinoId)?.nome || '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.carteiraOrigemId) return setError('Selecione a carteira de origem.');
    if (!form.carteiraDestinoId) return setError('Selecione a carteira de destino.');
    if (form.carteiraOrigemId === form.carteiraDestinoId) return setError('Origem e destino devem ser diferentes.');
    const valor = parseFloat(form.valor.replace(',', '.'));
    if (!valor || valor <= 0) return setError('Informe um valor válido.');

    setSaving(true);
    try {
      const res = await api.post('/transferencias', {
        ...form,
        valor,
      });
      toast.success(`Transferência de ${origemNome} → ${destinoNome} registrada!`);
      onSaved && onSaved(res.data);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Erro ao registrar transferência.';
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center nexus-modal-overlay p-4">
      <div className="nexus-modal w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <ArrowRightLeft size={16} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="font-bold text-slate-800 dark:text-slate-100 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Transferência entre Contas
              </h2>
              <p className="text-xs text-slate-400">Move saldo de uma carteira para outra</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Carteiras */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="fin-label">De (origem)</label>
              <select
                className="fin-input w-full"
                value={form.carteiraOrigemId}
                onChange={e => set('carteiraOrigemId', e.target.value)}
                required
              >
                <option value="">Selecionar...</option>
                {carteiras.map(c => (
                  <option key={c.id} value={c.id} disabled={c.id === form.carteiraDestinoId}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="fin-label">Para (destino)</label>
              <select
                className="fin-input w-full"
                value={form.carteiraDestinoId}
                onChange={e => set('carteiraDestinoId', e.target.value)}
                required
              >
                <option value="">Selecionar...</option>
                {carteiras.map(c => (
                  <option key={c.id} value={c.id} disabled={c.id === form.carteiraOrigemId}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Preview visual da transferência */}
          {form.carteiraOrigemId && form.carteiraDestinoId && (
            <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
              <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 truncate">{origemNome}</span>
              <ArrowRightLeft size={14} className="text-blue-500 flex-shrink-0" />
              <span className="text-xs font-semibold text-blue-700 dark:text-blue-300 truncate">{destinoNome}</span>
            </div>
          )}

          {/* Valor */}
          <div>
            <label className="fin-label">Valor</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">R$</span>
              <input
                className="fin-input w-full pl-8"
                placeholder="0,00"
                value={form.valor}
                onChange={e => set('valor', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Data */}
          <div>
            <label className="fin-label">Data</label>
            <input
              type="date"
              className="fin-input w-full"
              value={form.dataCompetencia}
              onChange={e => set('dataCompetencia', e.target.value)}
              required
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="fin-label">Descrição <span className="text-slate-400">(opcional)</span></label>
            <input
              className="fin-input w-full"
              placeholder="Ex: Reserva de emergência, pagamento..."
              value={form.descricao}
              onChange={e => set('descricao', e.target.value)}
            />
          </div>

          {/* Error */}
          {error && (
            <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>
          )}

          {/* Info */}
          <div className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
            <p className="text-xs text-slate-500 dark:text-slate-400">
              ℹ️ A transferência aparecerá no extrato de ambas as carteiras e <strong>não afeta</strong> receitas nem despesas.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="fin-btn-secondary flex-1">
              Cancelar
            </button>
            <button type="submit" disabled={saving} className="fin-btn-primary flex-1 flex items-center justify-center gap-2">
              {saving ? 'Registrando...' : (
                <><CheckCircle size={14} /> Registrar Transferência</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
